import Keycloak from "keycloak-js";
import {getFirstName} from "@/utils/stringHelper.js";
import {allowNextReload} from "@/composables/useUnsavedChangesGuard.js";

const publicPaths = ['/ficha-cadastro', '/public'];

// O keycloak-js 26 aceita exatamente { url, realm, clientId } (KeycloakServerConfig). As chaves
// em kebab-case que estavam aqui — "ssl-required", "resource", "public-client",
// "confidential-port" — são do formato keycloak.json do adapter, que a v26 deixou de carregar do
// servidor: a lib foi desacoplada e não é mais servida estaticamente por ele. Eram ignoradas.
const keycloakInstance = new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: "Triveni-AD",
    clientId: "frontend-client"
});
/*
"realm": "Triveni",
    "auth-server-url": "http://localhost:8080/",
    "ssl-required": "external",
    "resource": "prana",
    "public-client": true,
    "confidential-port": 0
*/
/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param {Function} onAuthenticatedCallback
 */



const Login = (onAuthenticatedCallback) => {

    keycloakInstance
        .init({
            onLoad: "check-sso",
            silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
            checkLoginIframe: false,
            pkceMethod: "S256"
        })
        .then(function (authenticated) {
            if (authenticated) {
                onAuthenticatedCallback();
            } else {
                const isPublicRoute = publicPaths.some(publicPath => window.location.pathname.startsWith(publicPath));

                if (isPublicRoute) {
                    onAuthenticatedCallback();
                } else {
                    // v26: login() devolve Promise — sem o catch, uma falha aqui vira
                    // unhandled rejection em vez de mensagem de erro.
                    keycloakInstance.login().catch((e) => {
                        console.error('Falha ao redirecionar para o login do Keycloak', e);
                    });
                }


                // fallback: força login se não autenticado
                //keycloakInstance.login();
            }
        })
        .catch((e) => {
            console.log('e',e)
            console.log('não autenticado')
            // Preciso que se a rota for uma publica permita o acesso

            console.dir(e);
            console.log(`keycloak init exception: ${e}`);
        });
};

const UserName = () => keycloakInstance?.tokenParsed?.preferred_username;
const UserMail = () => keycloakInstance?.tokenParsed?.email;

const Name = () => keycloakInstance?.tokenParsed?.name

const NameInitials = () => {
    const name = keycloakInstance?.tokenParsed?.name

    if (name) {
        let separatedName = name.split(" ");
        let names = []
        names.push(separatedName[0])

        if(separatedName.length > 1) {
            names.push(separatedName[separatedName.length-1])
        }

        separatedName = names

        const initials = separatedName.map(part => part.charAt(0).toUpperCase()).join("");
        return initials;
    }
}


const Cargo = () => keycloakInstance?.tokenParsed?.cargo

const ImageProfile = () => keycloakInstance?.tokenParsed?.imageProfile;

const CodigoPessoa = () => keycloakInstance?.tokenParsed?.codigo_pessoa

const UserId = () => keycloakInstance?.tokenParsed?.sub


const Pessoa = () => {
    return {
        conexosId: keycloakInstance?.tokenParsed?.codigo_pessoa,
        nome: keycloakInstance?.tokenParsed?.name
    }
}

const FirstName = (name) => {
        return getFirstName(name)
}

const Usuario = () => {
    console.log('keycloakInstance?.tokenParse', keycloakInstance?.tokenParsed)


    return {
        id: keycloakInstance?.tokenParsed?.sub,
        nome: keycloakInstance?.tokenParsed?.name,
        display: `${FirstName(keycloakInstance?.tokenParsed?.name)} (${keycloakInstance?.tokenParsed?.email})`
    }
}


const Token = () => keycloakInstance?.token;

// v26: logout() devolve Promise. Normalmente navega para fora antes de resolver, mas sem o catch
// uma falha (rede, servidor fora) vira unhandled rejection e o usuário não vê nada.
const LogOut = () =>
    keycloakInstance.logout().catch((e) => {
        console.error('Falha ao encerrar a sessão no Keycloak', e);
    });

const UserRoles = () => {
    if (keycloakInstance.resourceAccess === undefined) return undefined;
    if (keycloakInstance.resourceAccess["prana"] === undefined) return undefined;

    return keycloakInstance.resourceAccess["prana"].roles;
};

// `login()` e `logout()` passaram a SEMPRE devolver Promise no keycloak-js 26. Duas consequências
// aqui:
//
// 1. `doLogin` era `keycloakInstance.login` — uma referência de método solta. Usada como
//    `.catch(doLogin)`, recebia o objeto de ERRO no lugar das opções de login
//    (`login(erroDoRefresh)`), o que já estava errado antes da v26. Agora, com Promise, uma
//    falha ali também viraria unhandled rejection.
// 2. Por isso o wrapper: descarta o argumento e trata a rejeição do próprio login.
// Redirecionar para o login é a própria aplicação saindo da página, não o usuário
// abandonando o trabalho. Sem avisar a guarda de alterações não salvas, o navegador abre o
// diálogo dele do nada; e se o usuário clicar em "Cancelar" para não perder o que digitou, o
// redirect é abortado e a sessão fica com o token vencido — as requisições passam a devolver
// 401 e nem o Salvar funciona mais, ou seja, os dados que ele quis proteger deixam de ter
// como ser gravados.
const doLogin = () => {
    allowNextReload();

    return keycloakInstance.login().catch((e) => {
        console.error('Falha ao redirecionar para o login do Keycloak', e);
    });
};

// Sem successCallback: quem chama aguarda a Promise (`await UpdateToken()`) e só então lê o
// token. O callback existia para o interceptor do Axios montar o header Authorization, mas
// resolvia depois de a requisição já ter saído — veja o comentário em src/services/httpservice.js.
// Na falha do refresh, `doLogin` redireciona para o login; a Promise devolvida é a da navegação.
const updateToken = () => keycloakInstance.updateToken(5).catch(doLogin);

const isLoggedIn = () => !!keycloakInstance.token;

const isGranted = (role) => {
    if (keycloakInstance.resourceAccess === undefined) return undefined;
    if (keycloakInstance.realmAccess === undefined) return undefined;

    return keycloakInstance.realmAccess.roles.includes(role);
}


export const IsGranted = (role) => {
  if (Array.isArray(role)) {
    return role.some(r => isGranted(r));
  }

  return isGranted(role)
}

// Negação de permissão: true quando o usuário NÃO tem nenhuma das roles informadas E não é
// admin — o admin passa em toda checagem de permissão. Use no lugar de `!IsGranted(role)`, e
// no ramo positivo do mesmo fluxo use `IsGranted([role, 'admin'])` para os dois lados fecharem.
// Não serve para role de EXCLUSÃO (cliente, colaborador_satya, nonAllowedUsers do sidebar):
// ali o admin não deve ser dispensado, então continue negando IsGranted direto. O mesmo vale
// para 'peopleCulture': dado sensível de RH (salário, notas fiscais de prestador), sem admin.
export const IsNotGranted = (role) => !IsGranted(role) && !IsGranted('admin')


const KeycloakService = {
    CallLogin: Login,
    GetUserName: UserName,
    GetUserMail: UserMail,
    GetName: Name,
    GetInitials: NameInitials,
    GetCargo: Cargo,
    GetImageProfile: ImageProfile,
    GetCodigoPessoa: CodigoPessoa,
    GetUserId: UserId,
    GetAccesToken: Token,
    CallLogOut: LogOut,
    GetUserRoles: UserRoles,
    UpdateToken: updateToken,
    IsLoggedIn: isLoggedIn,
    IsGranted: IsGranted,
    IsNotGranted: IsNotGranted,
    GetPessoa: Pessoa,
    GetUsuario: Usuario
};

export default KeycloakService;




